import axios from 'axios';

import { paths } from 'src/routes/paths';

import formatError from './utils/format-error';
import { redirectToSsoLogin, clearSsoRedirectGuard } from './utils/sso-redirect';
import {
  retryInvalidCsrf,
  type CsrfRetryRequestConfig,
  attachCsrfRequestInterceptor,
} from './utils/csrf-interceptors';

const MY_INFO_ENDPOINT = '/users/my-info';

const axiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

attachCsrfRequestInterceptor(axiosInstance);

axiosInstance.interceptors.response.use(
  (response) => {
    clearSsoRedirectGuard();
    return response;
  },
  async (error) => {
    const originalRequest = error.config as CsrfRetryRequestConfig;
    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }
    const err = formatError(error);

    const publicPaths = [paths.root, paths.signIn, paths.authError];

    const isMyInfoBootstrap = originalRequest?.url?.includes(MY_INFO_ENDPOINT);
    const isExpiredSession =
      error.response?.status === 401 &&
      typeof window !== 'undefined' &&
      !publicPaths.includes(window.location.pathname) &&
      !isMyInfoBootstrap;

    if (isExpiredSession) {
      redirectToSsoLogin(window.location.pathname + window.location.search);
      return Promise.reject(error);
    }

    try {
      const csrfRetryResponse = await retryInvalidCsrf(err, originalRequest, axiosInstance);
      if (csrfRetryResponse) return csrfRetryResponse;
    } catch (refreshCsrfError) {
      console.error('CSRF token refresh failed:', refreshCsrfError);
      return Promise.reject(refreshCsrfError);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
